import { useCallback } from 'react';
import { Button, Form } from 'react-bootstrap';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import {
  CustomModalBody,
  CustomModalFooter,
} from 'src/components/custom_components/CustomModal';

import { Api } from 'src/models/Api.model';
import { I18NextNS } from 'src/models/Language.model';

import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';

import { emailPattern } from 'src/utils/validationRules';

import LoginContainer from './LoginContainer';

const ForgotPassword = () => {
  const history = useHistory();

  const { t } = useTranslation(I18NextNS.PAGES);

  const { showHttpRequestError } = useHandleHttpRequestError();

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      email: '',
    },
  });

  const handleSendResetCode = useCallback(
    async (email: string) => {
      try {
        await Api.remote.sendResetCode(email);
        history.push(`/reset-password-with-otp/${email}`);
      } catch (err) {
        showHttpRequestError(err);
      }
    },
    [history, showHttpRequestError],
  );

  const onSubmit: SubmitHandler<{
    email: string;
  }> = (data) => {
    handleSendResetCode(data.email);
  };

  return (
    <LoginContainer>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <CustomModalBody>
          <Form.Group className="mb-2">
            <Form.Label data-test="forgot-password-registered-email-label">
              <strong>{t('loginLogout.fields.registeredEmail')}</strong>
            </Form.Label>
            <Form.Control
              autoFocus
              id="email"
              type="text"
              placeholder={t('loginLogout.fields.emailPlaceholder')}
              autoComplete="off"
              {...register('email', {
                required: t('loginLogout.fields.emailRequired'),
                pattern: {
                  value: emailPattern,
                  message: t('loginLogout.fields.emailInvalid'),
                },
                maxLength: {
                  value: 254,
                  message: t('loginLogout.fields.emailMaxLength'),
                },
              })}
            ></Form.Control>

            {errors.email?.message && (
              <p
                className="text-danger"
                data-test="forgot-password-email-error"
              >
                {errors.email?.message}
              </p>
            )}
          </Form.Group>
        </CustomModalBody>
        <CustomModalFooter>
          <Button
            variant="primary-100"
            onClick={() => {
              history.goBack();
            }}
            data-test="forgot-password-back-button"
            data-track="auth.forgot-password.back"
            data-observe
          >
            {t('changePassword.back')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={isSubmitting}
            data-test="forgot-password-continue-button"
            data-track="auth.forgot-password.submit"
            data-observe
          >
            {isSubmitting
              ? t('changePassword.submitting')
              : t('changePassword.continue')}
          </Button>
        </CustomModalFooter>
      </Form>
    </LoginContainer>
  );
};

export default ForgotPassword;
